import sharedConfig from '@web-publish-id/shared/config'
import { ProviderAccessTokenCallback } from '../types'
import { BaseProvider } from './base-provider'
import { openPopup } from '../helper/utils'

/**
 * Google登录提供者
 */
export class GoogleProvider extends BaseProvider {
  protected readonly providerName = 'Google'
  protected clientId = sharedConfig.googleClientId

  /**
   * Google登录客户端
   */
  private client: any = null

  /**
   * 访问令牌回调
   */
  private accessTokenCallback: ProviderAccessTokenCallback | null = null

  /**
   * 错误回调
   */
  private accessTokenErrorCallback: (e: any) => void | null = null

  constructor() {
    super()
  }

  public installSDK(clientId?: string) {
    if (clientId) {
      this.clientId = clientId
    }

    return new Promise((resolve, reject) => {
      this._installSDK(resolve)
    })
  }

  private _installSDK(initDone: (val?: unknown) => any) {
    const clientId = this.clientId
    this.logger.info('Installing Google SDK', { clientId })

    const handleGoogleSignInCancel = () => {
      // 处理Google登录取消
      this.logger.info('Google login cancelled')
    }

    const initializeGoogleSignIn = (client_id) => {
      window.google.accounts.id.initialize({
        client_id,
        cancel_on_tap_outside: false, // 控制是否在提示之外进行点击时取消提示(关闭一键登录弹窗)，默认true
        auto_select: true, // 开启自动登录功能，默认false
        context: 'signin', // 指定为登录上下文
        ux_mode: 'popup', // 使用弹窗模式

        use_fedcm_for_prompt: true,

        callback: (res) => this.idResultCallback(res), // 验证成功回调
        cancel: handleGoogleSignInCancel,
      })

      this.client = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid profile email',
        callback: (res) => this.oAuthResultCallback(res),
        error_callback: (e) => this.oAuthErrorCallback(e),
      })

      initDone()
    }

    if (!window.google) {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client' // 加载客户端库
      script.async = true
      script.onload = () => initializeGoogleSignIn(clientId)
      document.head.appendChild(script)
    } else {
      initializeGoogleSignIn(clientId)
    }
  }

  public oAuth(success: ProviderAccessTokenCallback, fail: (e: any) => void) {
    this.logger.info('Google oAuth')
    // 如果已经有回调，则取消一键登录
    if (this.accessTokenCallback) {
      this.oneTapCancel()
      this.logger.info('cancel oneTap')
    }

    if (this.client) {
      this.accessTokenCallback = success
      this.accessTokenErrorCallback = fail
      this.client.requestAccessToken()
      return
    }
  }

  // 一键登录
  public oneTap(success: ProviderAccessTokenCallback, fail: (e: any) => void) {
    this.logger.info('Google oneTap')
    // 如果已经有回调，则不进行登录
    if (this.accessTokenCallback) {
      this.logger.info('Google is already in login process')
      return
    }

    if (window.google.accounts.id) {
      this.accessTokenCallback = success
      this.accessTokenErrorCallback = fail
      window.google.accounts.id.prompt((notification) => {
        console.log('notification :>> ', notification)
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // continue with another identity provider.
          this.accessTokenCallback = null
          this.accessTokenErrorCallback = null

          fail(new Error('Google oneTap cancel: ' + notification.getMomentType()))
        }
      })
    } else {
      this.logger.error('Google oneTap failed')
    }
  }

  // 一键登录取消
  public oneTapCancel() {
    window.google.accounts.id.cancel()
  }

  // 登出
  public signout() {
    window.google.accounts.id.disableAutoSelect()
  }

  /**
   * oAuth授权回调
   * @param result
   */
  private oAuthResultCallback(result: any) {
    this.logger.info('Google oAuth result', { result })

    if (this.accessTokenCallback) {
      this.accessTokenCallback(result.access_token)
      this.accessTokenCallback = null
    }
  }

  /**
   * id授权回调
   * @param result
   */
  private idResultCallback(result: any) {
    this.logger.info('Google id result', { result })

    if (this.accessTokenCallback) {
      this.accessTokenCallback(result.credential)
      this.accessTokenCallback = null
    }
  }

  private oAuthErrorCallback(e: any) {
    this.logger.error('Google oAuth error: ' + e.message, e)

    if (this.accessTokenErrorCallback) {
      this.accessTokenErrorCallback(new Error('Google login failed: ' + e.message))
      this.accessTokenErrorCallback = null
    }
    this.accessTokenCallback = null
  }

  private async oAuthByRedirect() {
    try {
      this.logger.info('Starting Google login')
      const baseUrl = 'https://accounts.google.com/o/oauth2/v2/auth'

      const state = {
        client_id: this.clientId,
        // redirect_uri: this.config.redirectUri,
        channel: 'google',
      }

      const params = new URLSearchParams({
        client_id: this.clientId,
        // redirect_uri: this.config.redirectUri,
        response_type: 'token',
        scope: 'openid profile email',
        prompt: 'consent',
        state: JSON.stringify(state),
      })

      const url = `${baseUrl}?${params.toString()}`

      const result = await openPopup(url)
      this.logger.info('Google login successful')
      return result
    } catch (error) {
      this.logger.error('Google login failed', { error })
      throw error
    }
  }
}
